// Re-initialisation for app widgets that only boot on a page load. A pjax
// navigation brings an app's markup in with the new view, and pjax-head.js
// brings in its embeds' data and any script the new page needs, but an app
// that sets itself up once, on DOMContentLoaded or as its element connects,
// never sees the new view. Those apps get kicked here, after each navigation:
// Judge.me by its own APIs, Estimated Delivery by connecting its element again.
//
// We do NOT dispatch a synthetic DOMContentLoaded — Shopify Pay's prefetch
// engine and Shopify's speculation rules listen for it and re-emit their full
// prefetch payload into <head> each time.

import { isJavaScript } from "@theme/pjax-head";

/** Start watching for what the kicks need to find later. Call once, as the router starts. */
export function watchApps() {
  watchReviewManagerURL();
}

/** Kick the apps whose widgets are in a view that has just come in, from its parsed page. */
export function kickApps(view, page) {
  if (!view) return;
  try {
    refreshEstimatedDelivery(page);
  } catch (err) {
    console.warn("[pjax] refreshing Estimated Delivery failed", err);
  }
  Promise.resolve()
    .then(() => kickJudgeMeReviewWidget(view, page))
    .catch((err) => {
      console.warn("[jdgm-pjax] kickJudgeMeReviewWidget rejected", err);
    });
  Promise.resolve()
    .then(() => kickJudgeMePreviewBadges(view, page))
    .catch((err) => {
      console.warn("[jdgm-pjax] kickJudgeMePreviewBadges rejected", err);
    });
}

// Essentials Estimated Delivery is all in its app embed, a body embed:
// <essential-estimated-delivery-app-embed> carries the page's product in its
// attributes, and as it connects it matches the product to a delivery rule
// and draws the widget, into the product page's app block (which shows only
// what the embed gives it) or the product form, and on a collection page
// into the product cards. It only does that as it connects, and body embeds
// keep the first page's markup, so a page reached by pjax got no widget, or
// the first product's.
//
// After each navigation, then, the embed takes the attributes of the new
// page's copy and is connected again, which runs that setup against the new
// view, as a page load would. Its disconnect puts back the window.fetch and
// history.pushState / replaceState it wrapped as it first connected, which
// would also drop whatever wrapped them after it (BOGOS's cart watcher wraps
// fetch), and its connect wraps them again; so for the moment it takes, they
// are held as they are. The wrappers it installed first stay in place, and
// they call this same element.
const DELIVERY_EMBED = "essential-estimated-delivery-app-embed";

function refreshEstimatedDelivery(page) {
  const embed = document.querySelector(DELIVERY_EMBED);
  const next = page?.querySelector(DELIVERY_EMBED);
  if (!embed?.parentNode || !next) return;

  for (const { name, value } of next.attributes) {
    if (embed.getAttribute(name) !== value) embed.setAttribute(name, value);
  }
  for (const { name } of [...embed.attributes]) {
    if (!next.hasAttribute(name)) embed.removeAttribute(name);
  }

  const { parentNode, nextSibling } = embed;
  holding(
    [
      [window, "fetch"],
      [history, "pushState"],
      [history, "replaceState"],
    ],
    () => {
      embed.remove();
      parentNode.insertBefore(embed, nextSibling);
    }
  );
}

/**
 * Run `fn` with these properties held: reading one gives what it held
 * before, and assigning to one does nothing. If one can't be held (something
 * made it non-configurable), nothing is changed and `fn` doesn't run.
 */
function holding(properties, fn) {
  const held = [];
  const release = () => {
    for (const [owner, key, own] of held.reverse()) {
      if (own) Object.defineProperty(owner, key, own);
      else delete owner[key];
    }
  };

  try {
    for (const [owner, key] of properties) {
      const own = Object.getOwnPropertyDescriptor(owner, key);
      const value = owner[key];
      Object.defineProperty(owner, key, { configurable: true, enumerable: own?.enumerable ?? true, get: () => value, set() {} });
      held.push([owner, key, own]);
    }
  } catch {
    release();
    return;
  }

  try {
    fn();
  } finally {
    release();
  }
}

// Judge.me's preview badge (stars + "XX reviews") is the small summary
// rendered by widget_common.js's `jdgm.batchRenderBadges`. The function is
// exposed on `window.jdgm` once widget_common.js boots, but is only called
// once per page lifecycle — from Judge.me's app-block inline boot script in
// `{{ content_for_header }}`, which DOMContentLoaded-gates via
// `jdgm.$(function(){...})`. On PJAX nav that boot doesn't re-fire, so newly
// swapped-in `.jdgm-prev-badge` placeholders never get fetched and stay
// hidden (the placeholder ships with `style="display:none"`; only
// `batchRenderBadges` → `customizeBadges` reveals it).
//
// Re-call `batchRenderBadges` ourselves against the new view. The API takes
// `[{ productHandle, badgePlaceholder }, ...]` — handle lookup follows the
// most common encodings used by Judge.me + this theme: data-* on the badge,
// a `[data-product-handle]` ancestor, a nearby `/products/<handle>` link,
// or (PDP fallback) the current URL.
const findBadgeProductHandle = (badge) => {
  if (badge.dataset.productHandle) return badge.dataset.productHandle;
  if (badge.dataset.handle) return badge.dataset.handle;
  const ancestor = badge.closest("[data-product-handle], [data-handle]");
  if (ancestor) {
    const h = ancestor.dataset.productHandle || ancestor.dataset.handle;
    if (h) return h;
  }
  const link =
    badge.closest("a[href*='/products/']") ||
    badge.parentElement?.querySelector("a[href*='/products/']");
  if (link) {
    const m = link.getAttribute("href").match(/\/products\/([^/?#]+)/);
    if (m) return m[1];
  }
  if (/^\/products\/[^/]+/.test(location.pathname)) {
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    if (m) return m[1];
  }
  return null;
};

// Judge.me's app embed only loads its widget stack on demand: loader.js, on
// initial DOMContentLoaded, checks whether any preview badges or widgets
// exist on the page. On a non-PDP origin (e.g. home), nothing matches so
// none of the runtime bundles load. On home → PDP via PJAX, loader.js
// doesn't re-run, so `window.jdgm`, `window.jQuery`, AND any badge-specific
// bundles are absent.
//
// The badge flow needs three scripts, in order:
//   1. widget_base.js   — Judge.me's bundled jQuery (`window.jQuery`).
//   2. widget_common.js — defines `jdgm.batchRenderBadges` (the AJAX + render
//                         orchestrator). Wrapped in `jdgm.$(function(){...})`,
//                         so widget_base must run first or it throws
//                         "jdgm.$ is not a function".
//   3. widget/main.js   — defines `jdgm.customizeBadges` and
//                         `jdgm._addEmptyPreviewBadge`. widget_common's batch
//                         completion callback `d(t)` does
//                         `jdgm.customizeBadges(), jdgm.asyncEach(...)` — if
//                         customizeBadges is undefined that throws and the
//                         "remove inline display:none" reveal never runs.
//                         Only on Judge.me's CDN; Shopify extension mirror
//                         404s for this file.
//
// Loader.js uses CDN_HOST = "https://cdnwidget.judge.me/" for runtime bundle
// loads; we mirror that here. base.js and common.js can be loaded from the
// Shopify mirror (faster, same origin as the rest of the storefront), so we
// derive that base from any existing Judge.me script.
const JDGM_CDN_HOST = "https://cdnwidget.judge.me/";
const findJudgemeAssetBase = (root) => {
  if (!root) return null;
  for (const s of root.querySelectorAll('script[src*="judgeme"]')) {
    const m = s.src.match(/^(.*\/judgeme-[^/]+\/assets\/)/);
    if (m) return m[1];
  }
  return null;
};
const waitForJdgmBadgeApi = (timeoutMs = 5000) =>
  new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      if (
        window.jdgm &&
        typeof window.jdgm.batchRenderBadges === "function" &&
        typeof window.jdgm.customizeBadges === "function"
      ) {
        resolve(true);
        return;
      }
      if (Date.now() - start > timeoutMs) {
        resolve(false);
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  });
const injectJdgmScript = (src) =>
  new Promise((resolve, reject) => {
    for (const s of document.querySelectorAll("script[src]")) {
      if (s.src !== src) continue;
      if (s.dataset.pjaxJdgmLoaded === "1") {
        resolve();
        return;
      }
      s.addEventListener("load", () => resolve(), { once: true });
      s.addEventListener("error", () => reject(new Error(`load failed: ${src}`)), {
        once: true,
      });
      return;
    }
    const fresh = document.createElement("script");
    fresh.src = src;
    fresh.async = true;
    fresh.crossOrigin = "anonymous";
    fresh.dataset.pjaxJdgmBadgeBootstrap = "1";
    fresh.onload = () => {
      fresh.dataset.pjaxJdgmLoaded = "1";
      resolve();
    };
    fresh.onerror = () => reject(new Error(`load failed: ${src}`));
    document.head.appendChild(fresh);
  });
let widgetCommonReadyPromise = null;
const ensureWidgetCommon = (fetchedPage) => {
  if (
    window.jdgm &&
    typeof window.jdgm.batchRenderBadges === "function" &&
    typeof window.jdgm.customizeBadges === "function"
  ) {
    return Promise.resolve(true);
  }
  if (widgetCommonReadyPromise) return widgetCommonReadyPromise;
  const base =
    findJudgemeAssetBase(document) || findJudgemeAssetBase(fetchedPage);
  if (!base) return Promise.resolve(false);
  widgetCommonReadyPromise = (async () => {
    try {
      if (!window.jQuery) {
        await injectJdgmScript(base + "widget_base.js");
      }
      if (typeof window.jdgm?.batchRenderBadges !== "function") {
        await injectJdgmScript(base + "widget_common.js");
      }
      if (typeof window.jdgm?.customizeBadges !== "function") {
        await injectJdgmScript(JDGM_CDN_HOST + "widget/main.js");
      }
      return await waitForJdgmBadgeApi();
    } catch (err) {
      console.warn("[jdgm-pjax] widget_common bootstrap failed", err);
      widgetCommonReadyPromise = null;
      return false;
    }
  })();
  return widgetCommonReadyPromise;
};

// Judge.me's widget_common.js init runs an `hide_badge_preview_if_no_reviews`
// scan that calls jQuery `.toggle(reviewCount > 0)` on cached `.jdgm-prev-badge`
// wrappers — when triggered after our batchRenderBadges call (or before the
// AJAX populates `data-number-of-reviews`), the badge ends up with an inline
// `style="display: none;"` even though the inner stars + text content is in
// place. The hide also fires inside the customizeBadges path that runs after
// every batch. Counter it with a short-lived MutationObserver on each badge:
// if anything writes `display: none` to its inline style, clear it. Bounded
// to a few seconds so we don't leak observers across navs.
const keepBadgesVisible = (badges, durationMs = 4000) => {
  const unhide = (el) => {
    if (el.style.display === "none") {
      el.style.removeProperty("display");
    }
  };
  badges.forEach(unhide);
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === "attributes" && m.attributeName === "style") {
        unhide(m.target);
      }
    }
  });
  for (const badge of badges) {
    observer.observe(badge, {
      attributes: true,
      attributeFilter: ["style"],
    });
  }
  setTimeout(() => observer.disconnect(), durationMs);
};

const kickJudgeMePreviewBadges = async (view, fetchedPage) => {
  if (!view) return;
  const badges = view.querySelectorAll(".jdgm-prev-badge");
  if (!badges.length) return;
  const ready = await ensureWidgetCommon(fetchedPage);
  if (!ready) return;
  const items = [];
  const trackedBadges = [];
  for (const badge of badges) {
    if (badge.dataset.pjaxJdgmBadgeKicked === "1") continue;
    const handle = findBadgeProductHandle(badge);
    if (!handle) continue;
    badge.dataset.pjaxJdgmBadgeKicked = "1";
    items.push({ productHandle: handle, badgePlaceholder: badge });
    trackedBadges.push(badge);
  }
  if (!items.length) return;
  window.jdgm.batchRenderBadges(items);
  keepBadgesVisible(trackedBadges);
};

// Judge.me's revamp review widget hardwires init to DOMContentLoaded via a
// singleton with an `initialized` flag and no window-attached handle, so
// there's no public re-init call. Its ReviewWidgetManager class IS exported
// though, and modules are URL-cached, so we dynamically import the same
// chunk Judge.me already preloaded and instantiate it ourselves against the
// new view's container. Hash in the URL changes per Judge.me deploy, so we
// read it from the live modulepreload link rather than hardcoding.
// review_widget.js statically imports ReviewWidgetManager-<hash>.js, but
// the browser doesn't emit a modulepreload tag for static-import deps, so
// the URL is only visible via the performance resource entries. Resource
// entries are also fragile: the default buffer holds 250 entries and a
// Shopify PDP can blow past that, and Shopify/Judge.me may also call
// performance.clearResourceTimings(). Use a PerformanceObserver with
// buffered:true to capture the URL the first time it's seen and cache it
// for the lifetime of the page — entries delivered to an observer can't be
// cleared out from under us once captured.
let reviewManagerURL = null;
const captureReviewManagerURL = (entries) => {
  for (const entry of entries) {
    if (
      !reviewManagerURL &&
      /\/ReviewWidgetManager-[^/]+\.js(?:\?|$)/.test(entry.name)
    ) {
      reviewManagerURL = entry.name;
    }
  }
};
const watchReviewManagerURL = () => {
  try {
    captureReviewManagerURL(performance.getEntriesByType("resource"));
    if (!reviewManagerURL) {
      const observer = new PerformanceObserver((list) => {
        captureReviewManagerURL(list.getEntries());
        if (reviewManagerURL) observer.disconnect();
      });
      observer.observe({ type: "resource", buffered: true });
    }
  } catch {}
};

// When the user starts on a page without Judge.me containers (home page,
// for example), Judge.me's app embed never loads review_widget.js, so the
// ReviewWidgetManager-<hash>.js URL is nowhere in the document or
// performance log. We discover it on-demand: review_widget.js IS referenced
// as a <script type="module"> in the fetched PDP HTML's <head> (outside
// our pjax-zone, but still parseable from to.page). Fetch its source as
// text and regex out the relative ReviewWidgetManager import to resolve a
// concrete URL. The module is identical to what Judge.me would have served,
// so its static-import chain (Vue, layout, ReviewWidget, etc.) loads as
// side effects of importing it.
const findReviewWidgetScriptURL = (fetchedPage) => {
  return findReviewWidgetURL(document) || findReviewWidgetURL(fetchedPage);
};
let reviewManagerURLPromise = null;
const ensureReviewManagerURL = (fetchedPage) => {
  if (reviewManagerURL) return Promise.resolve(reviewManagerURL);
  if (reviewManagerURLPromise) return reviewManagerURLPromise;

  // Try a modulepreload link in case Judge.me ever emits one.
  const link = document.querySelector(
    'link[rel="modulepreload"][href*="ReviewWidgetManager"]'
  );
  if (link?.href) {
    reviewManagerURL = link.href;
    return Promise.resolve(reviewManagerURL);
  }

  const widgetURL = findReviewWidgetScriptURL(fetchedPage);
  if (!widgetURL) {
    console.warn(
      "[jdgm-pjax] could not find review_widget.js to discover ReviewWidgetManager"
    );
    return Promise.resolve(null);
  }

  reviewManagerURLPromise = (async () => {
    try {
      const res = await fetch(widgetURL, { credentials: "omit" });
      if (!res.ok) {
        console.warn(
          "[jdgm-pjax] fetch of review_widget.js failed",
          res.status,
          widgetURL
        );
        return null;
      }
      const text = await res.text();
      const match = text.match(
        /["'](?:\.\/)?(ReviewWidgetManager-[A-Za-z0-9_-]+\.js)["']/
      );
      if (!match) {
        console.warn(
          "[jdgm-pjax] review_widget.js has no ReviewWidgetManager-<hash>.js import — Judge.me may have renamed the chunk"
        );
        return null;
      }
      reviewManagerURL = new URL(match[1], widgetURL).href;
      return reviewManagerURL;
    } catch (err) {
      console.warn("[pjax] failed to discover ReviewWidgetManager URL", err);
      return null;
    } finally {
      reviewManagerURLPromise = null;
    }
  })();
  return reviewManagerURLPromise;
};

// Judge.me ships ReviewWidgetManager as a minified ES-module export whose
// binding name (historically `R`) is reassigned by their bundler on every
// deploy, so hard-coding `mod.R` silently breaks the day Judge.me redeploys.
// Resolve the class by shape instead — the export we want is a constructor
// whose prototype carries the `initialize()` method we call. Keep `mod.R` and
// `mod.default` as fast-path fallbacks, and shout if nothing matches so the
// next rename surfaces in the console instead of a silently empty widget.
const resolveReviewWidgetManager = (mod) => {
  if (!mod) return null;
  const byShape = Object.values(mod).find(
    (exp) =>
      typeof exp === "function" &&
      exp.prototype &&
      typeof exp.prototype.initialize === "function"
  );
  if (byShape) return byShape;
  if (typeof mod.R === "function") return mod.R;
  if (typeof mod.default === "function") return mod.default;
  console.error(
    "[jdgm-pjax] could not resolve ReviewWidgetManager from module exports",
    Object.keys(mod)
  );
  return null;
};

let reviewWidgetManagerPromise = null;
const getReviewWidgetManager = (fetchedPage) => {
  if (reviewWidgetManagerPromise) return reviewWidgetManagerPromise;
  reviewWidgetManagerPromise = (async () => {
    const url = await ensureReviewManagerURL(fetchedPage);
    if (!url) return null;
    const mod = await import(url);
    return resolveReviewWidgetManager(mod);
  })().catch((err) => {
    reviewWidgetManagerPromise = null;
    console.warn("[pjax] failed to import Judge.me ReviewWidgetManager", err);
    return null;
  });
  return reviewWidgetManagerPromise;
};

// Judge.me's app embed renders its setup inline in <head>:
//   <script>window.jdgmSettings = {...}</script>
//   <script class="jdgm-script">window.jdgm = window.jdgm || {}; ... jdgm.docReady(loadWidgetCSS)</script>
// pjax-head.js keeps that embed in step with each page, but it's the same on
// every page, so it never runs again — and replaying it is what re-arms the
// widget stylesheet loader (jdgm.docReady fires at once after load) for a
// page that has widgets. Replay JUST the inline jdgm scripts from the fetched
// PDP's <head>. External <script src> tags are NOT touched here — they're
// what caused the prefetch flood earlier when we touched them all.
const runJudgeMeInlineHeadScripts = (fetchedPage) => {
  if (!fetchedPage?.head) return;
  // Remove the previous nav's replayed scripts before re-appending — they
  // were just one-shot setup and would otherwise accumulate one stack per
  // PDP nav.
  for (const old of document.head.querySelectorAll(
    "script[data-pjax-jdgm-inline]"
  )) {
    old.remove();
  }
  for (const node of fetchedPage.head.querySelectorAll("script:not([src])")) {
    if (!isJavaScript(node)) continue;
    const source = node.textContent || "";
    if (!/jdgm/.test(source)) continue;
    const fresh = document.createElement("script");
    for (const { name, value } of node.attributes) fresh.setAttribute(name, value);
    fresh.textContent = `try{\n${source}\n}catch(_){}`;
    fresh.dataset.pjaxJdgmInline = "1";
    document.head.appendChild(fresh);
  }
};

// Judge.me's revamp widget is a Vite build: its CSS ships as separate hashed
// chunks (layout-<hash>.css, ReviewWidget-<hash>.css, ...) that neither the
// page HTML nor any JS module references directly. On a direct load, loader.js
// resolves them by fetching `manifest.json` and walking the container's
// `data-entry-key` entry — its `css` list plus, recursively, the `css` of
// everything in its `imports` — appending a <link> for each BEFORE it injects
// review_widget.js. loader.js only runs on the initial DOMContentLoaded, and
// injectReviewWidgetScript() below appends review_widget.js directly, so on a
// non-PDP -> PDP nav that walk never happens: the widget mounts with all its
// markup and none of its layout CSS.
//
// Replay the same walk. These <link>s land in <head>, outside the router view,
// so once loaded they survive every later navigation.
let judgeMeManifestPromise = null;
const loadJudgeMeManifest = (base) => {
  if (judgeMeManifestPromise) return judgeMeManifestPromise;
  judgeMeManifestPromise = fetch(base + "manifest.json")
    .then((res) => (res.ok ? res.json() : null))
    .catch((err) => {
      console.warn("[jdgm-pjax] manifest.json fetch failed", err);
      return null;
    });
  return judgeMeManifestPromise;
};
const appendStylesheet = (href) => {
  for (const link of document.querySelectorAll('link[rel="stylesheet"][href]')) {
    if (link.href === href) return;
  }
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
};
const ensureJudgeMeWidgetCss = async (containers) => {
  const jdgm = window.jdgm;
  const base = jdgm?.CDN_BASE_URL;
  if (!base) return;

  // The widget_v3 sheets loader.js pulls in from its own docReady scan. That
  // scan matches against the server-rendered legacy markup, which the revamp
  // widget replaces on mount, so it can't be re-run after the fact. Request
  // them directly instead — jdgm.loadCSS dedupes via its own requestedUrls.
  if (typeof jdgm.loadCSS === "function" && typeof jdgm.widgetPath === "function") {
    for (const file of ["base.css", "main.css", "media.css", "form.css"]) {
      try {
        jdgm.loadCSS(jdgm.widgetPath(file));
      } catch {}
    }
  }

  const manifest = await loadJudgeMeManifest(base);
  if (!manifest) return;
  const walked = new Set();
  const walk = (key) => {
    if (!key || walked.has(key)) return;
    walked.add(key);
    const entry = manifest[key];
    if (!entry) return;
    for (const file of entry.css || []) appendStylesheet(base + file);
    for (const imported of entry.imports || []) walk(imported);
  };
  for (const container of containers) walk(container.dataset.entryKey);
};

// Judge.me's app embed only emits a small loader.js bootstrap in <head> on
// every page. loader.js loads review_widget.js at DOMContentLoaded only if
// .jdgm-review-widget containers exist. On home → PDP via PJAX, loader.js
// already ran (with no containers) and won't re-run, so review_widget.js
// never gets loaded. The two live in the same /judgeme-<ver>/assets/
// directory, so derive review_widget.js's URL by swapping the loader.js
// filename, then inject the script tag the way a direct PDP load would.
// Judge.me's singleton.init() then scans the live DOM and mounts the widget.
// Subsequent PDP navs are handled by direct ReviewWidgetManager
// instantiation (the singleton's `initialized` flag short-circuits its own
// init).
let reviewWidgetScriptInjected = false;
const findReviewWidgetURL = (root) => {
  if (!root) return null;
  for (const s of root.querySelectorAll("script[src]")) {
    if (/review_widget\.js(?:\?|$)/.test(s.src)) return s.src;
  }
  const loader = root.querySelector(
    'script[src*="judgeme"][src*="loader.js"]'
  );
  if (loader) {
    return loader.src.replace(/\/loader\.js(?:\?.*)?$/, "/review_widget.js");
  }
  return null;
};
const injectReviewWidgetScript = (fetchedPage) => {
  if (reviewWidgetScriptInjected) return false;
  // Already loaded? Skip.
  for (const s of document.querySelectorAll("script[src]")) {
    if (/review_widget\.js(?:\?|$)/.test(s.src)) {
      reviewWidgetScriptInjected = true;
      return false;
    }
  }
  const url = findReviewWidgetURL(document) || findReviewWidgetURL(fetchedPage);
  if (!url) return false;
  const fresh = document.createElement("script");
  fresh.type = "module";
  fresh.src = url;
  fresh.crossOrigin = "anonymous";
  fresh.dataset.pjaxJdgmBootstrap = "1";
  document.head.appendChild(fresh);
  reviewWidgetScriptInjected = true;
  return true;
};

// Fallback path: re-run Judge.me's OWN widget bootstrap by injecting
// review_widget.js with a cache-busting query. This defeats the ES-module
// registry so a fresh singleton boots and scans the live DOM — the same path
// that mounts the widget on a direct page load. Only used when we can't drive
// ReviewWidgetManager ourselves, so a future Judge.me change can't leave the
// widget silently missing.
let reviewWidgetRefreshCount = 0;
const reinjectReviewWidgetFresh = (fetchedPage) => {
  const url = findReviewWidgetURL(document) || findReviewWidgetURL(fetchedPage);
  if (!url) return false;
  reviewWidgetRefreshCount += 1;
  const bust = `${url.includes("?") ? "&" : "?"}pjax=${reviewWidgetRefreshCount}`;
  const fresh = document.createElement("script");
  fresh.type = "module";
  fresh.src = url + bust;
  fresh.crossOrigin = "anonymous";
  fresh.dataset.pjaxJdgmRefresh = "1";
  document.head.appendChild(fresh);
  return true;
};

// A container still needs mounting if we never marked it done and Judge.me
// hasn't rendered anything into it yet.
const widgetNeedsMount = (container) =>
  container.dataset.pjaxJdgmInitialized !== "1" &&
  container.childElementCount === 0;

const kickJudgeMeReviewWidget = async (view, fetchedPage) => {
  if (!view) return;
  const containers = view.querySelectorAll(
    ".jdgm-review-widget[data-entry-point]"
  );
  if (!containers.length) return;

  // Populate window.jdgm.data.reviewWidget[productId] and jdgmSettings
  // before any widget init reads them.
  runJudgeMeInlineHeadScripts(fetchedPage);

  // Then the stylesheets, before anything mounts — loader.js orders it the
  // same way so the widget doesn't paint unstyled.
  await ensureJudgeMeWidgetCss(containers);

  // First PDP nav this session: let Judge.me's own bootstrap initialize the
  // widget. The singleton's init() runs once on script load and scans the
  // live DOM, so we don't need to do anything else for this nav.
  if (!reviewManagerURL && !reviewWidgetScriptInjected) {
    if (injectReviewWidgetScript(fetchedPage)) {
      for (const container of containers) {
        container.dataset.pjaxJdgmInitialized = "1";
      }
      return;
    }
    // fall through to direct-instantiation fallback
  }

  // Subsequent PDP navs: drive ReviewWidgetManager directly.
  const Manager = await getReviewWidgetManager(fetchedPage);
  let mounted = 0;
  if (Manager) {
    for (const container of containers) {
      if (container.dataset.pjaxJdgmInitialized === "1") continue;
      container.dataset.pjaxJdgmInitialized = "1";
      try {
        await new Manager(container).initialize();
        mounted += 1;
      } catch (err) {
        console.warn("[jdgm-pjax] widget init failed", err);
        // Let the fallback (and future navs) retry this container.
        delete container.dataset.pjaxJdgmInitialized;
      }
    }
  } else {
    console.warn(
      "[jdgm-pjax] ReviewWidgetManager unavailable — falling back to a fresh Judge.me bootstrap"
    );
  }

  // If nothing mounted but empty widget containers remain, re-run Judge.me's
  // own bootstrap so the widget still appears.
  if (!mounted && Array.from(containers).some(widgetNeedsMount)) {
    reinjectReviewWidgetFresh(fetchedPage);
  }
};
